import _ from 'lodash';

import { Get, urls } from 'assets/js/api';

// FetchUsers fetches users for a site and stores returns either the users or
// the total count of users. It gives you options for how to handle both error
// and loading states, and is dependent on the context of the Vue component.
export const FetchUsers = async (ctx, params, siteId, portalId) => {
  const {
    accountNumberDesc = null, // Account number/description
    emitError = true, // Emit error state (ctx)
    emitLoading = null, // Emit loading state (ctx)
    excludeUserIds = null, // List of user IDs to exclude from results
    extraSleep = null, // Sleep before fetch (milliseconds)
    fast = null, // Use the fast API that only returns user_id, name, and email
    includeActive = null, // Include the active status of the user.
    includeBarcode = null, // Include user barcode
    includeExpired = null, // Include expired trainings
    includeHasCCMultiuse = null, // Include CC Multiuse Status
    includeCredentialAffixes = null, // Include Credential Affixes
    includeRoles = null, // Include the site user role in fast mode
    includeUnsubscribed = null, // Include email unsubscribed status
    limit = 99999999, // Number of items to return
    localError = null, // Set local error state (ctx)
    localLoading = null, // Set local loading state (ctx)
    offset = 0, // Offset cursor
    returnUsers = null, // Return users or count
    role = null,
    slim = null, // Verbose response
    slimReservables = null, // Make the reservables slim in approvers
    sortBy = null, // Sort by field
    str = null, // Search string
    supervisorId = null, // Filter by supervisor
    throwError = null, // Throw errors if any are caught
    training = null, // Return trainings
    trainingId = null, // Return trainings for specific ID
    userBotsOnly = null, // Return only user bots
    noUserGroups = null, // Return only users with no user groups
    active = null, // Return users with specific active property

    // Override for fetching portal trainings from a site's perspective.
    trainingPortalId = null,
    userGroup = null, // Usergroup filter; int for usergroup_id
    userId = null, // User ID string
    userIds = null, // User IDs array of strings, only works with fast
  } = params;

  if (_.isNull(siteId) && _.isNull(portalId)) {
    throw Error('FetchUsers: siteId or portalId undefined');
  }
  if (_.isNull(limit)) throw Error('FetchUsers: limit undefined');
  if (_.isNull(offset)) throw Error('FetchUsers: offset undefined');

  if (extraSleep) {
    await ctx.sleep(extraSleep);
  }

  // For local fetches for components, don't set the loading state
  // because the component itself should handle that.
  if (!returnUsers) {
    if (emitLoading) ctx.$emit('emitLoading', true);
    if (localLoading) ctx.loading = true;
  }

  let users;
  const opts = {
    params: {
      limit,
      offset,
      return_count: true,
    },
  };

  if (!fast) {
    if (accountNumberDesc) {
      opts.params.query_account_number_desc = accountNumberDesc;
    }
    if (sortBy) opts.params.sort_mtd = sortBy;
    if (userGroup) opts.params.usergroup = userGroup;
    if (role) opts.params.role = role;
    if (slim) opts.params.slim = slim;
    if (slimReservables) opts.params.slim_reservables = slimReservables;
    if (str) opts.params.search = str;
    if (userId) opts.params.user_id = userId;
    if (supervisorId) opts.params.supervisor_id = supervisorId;
    if (training) opts.params.training = true;
    if (trainingId) opts.params.training_id = trainingId;
    if (trainingPortalId) opts.params.training_portal_id = trainingPortalId;
    if (includeActive) opts.params.include_active = true;
    if (includeExpired) opts.params.include_expired = true;
    if (includeUnsubscribed) opts.params.include_unsubscribed = true;
    if (includeHasCCMultiuse) opts.params.include_has_cc_multiuse = true;
    if (includeBarcode) opts.params.include_barcode = true;
    if (includeCredentialAffixes) opts.params.include_credential_affixes = true;
    if (userBotsOnly) opts.params.user_bots_only = true;
    if (noUserGroups) opts.params.no_user_groups = true;
    if (active !== null) opts.params.active = active;
  } else {
    if (accountNumberDesc) {
      opts.params.query_account_number_desc = accountNumberDesc;
    }
    if (excludeUserIds) opts.params.exclude_user_ids = excludeUserIds;
    if (str) opts.params.query = str;
    if (role) opts.params.role = role;
    if (supervisorId) opts.params.supervisor_id = supervisorId;
    if (userGroup) opts.params.usergroup_id = userGroup;
    if (userId) opts.params.user_id = userId;
    if (userIds) opts.params.user_ids = userIds;
    if (includeActive) opts.params.include_active = true;
    if (includeRoles) opts.params.include_roles = includeRoles;
    if (includeBarcode) opts.params.include_barcode = true;
    if (userBotsOnly) opts.params.user_bots_only = true;
    if (active !== null) opts.params.active = active;
    delete opts.params.return_count;
  }

  let url = urls.SITE_USERS(siteId);
  if (portalId) url = urls.PORTAL_USERS(portalId);
  if (fast) url = urls.USER_SEARCH;
  if (fast && siteId) opts.params.site_id = siteId;
  if (fast && portalId) opts.params.portal_id = portalId;

  try {
    users = await Get(url, opts);
  } catch (err) {
    if (localLoading) ctx.loading = false;
    if (emitLoading) ctx.$emit('emitLoading', false);
    if (localError) ctx.err = err;
    if (emitError) ctx.$emit('emitErr', err);
    if (throwError) throw err;
  }

  // Site/portal userify the list of users to what we expect from the old,
  // slow end point.
  if (fast) users.data = _.map(users.data, (user) => Object({ user }));

  if (returnUsers && !userId) return users.data;
  if (returnUsers && userId && !fast) return [users.data];
  if (returnUsers && userId && fast) return users.data;

  if (!userId) ctx.users = users.data;
  if (userId) ctx.users = [users.data];
  if (localLoading) ctx.loading = false;
  if (emitLoading) ctx.$emit('emitLoading', false);
  return users.numitems;
};
